"use client";

import React, { useEffect, useState } from "react";

import styles from "./TireSizeComponent.module.scss";
import Text from "@components/atoms/Text";
import TireSizeTextBlock from "@components/molecules/TireSizeTextBlock";
import TireSizeButtons from "@components/molecules/TireSizeButtons";
import TireSizeVariantCard from "@components/molecules/TireSizeVariantCard";
import BootCapacityTextBox from "@components/atoms/BootCapacityTextBox";
import ImageResponsiveAssets from "@components/atoms/ImageResponsiveAssets";

import { Carousel } from "@components/molecules/Carousel";
import { formatDeliveryDate } from "@utils/formatDate";
import { formatPrice, numberToWords } from "@utils/numberFormatter";
import { parseTireSizeArrow } from "@utils/tyreNameUtils";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";
import { getCarAvailability } from "@utils/carAvailability";

export default function TireSizeComponent({
  modelTyresize,
  cityName,
  carAvailability,
  isLoggedIn,
  makeName,
  modelName,
  pageType,
}: {
  modelTyresize?: any[];
  cityName: string;
  isSellable: boolean;
  carAvailability: string;
  isLoggedIn: boolean;
  makeName?: string;
  modelName?: string;
  pageType?: string;
}) {
  const [convertedTyreSize, setConvertedTyreSize] = useState<{
    [key: string]: any;
  }>({});
  const [activeTab, setActiveTab] = useState<string>("");

  // Event tracking for tire size tab selection
  const handleClick = async (tyreCode: string) => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || PAGE_TYPES.DIMENSIONS_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_tyre_size",
        selected_value: tyreCode,
        car_availability: carAvailability,
        city_name: cityName,
        vehicle_make: makeName,
        vehicle_model: modelName,
      },
    });
    setActiveTab(tyreCode);
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = () => {
      const converted = modelTyresize?.reduce(
        (acc: { [key: string]: any }, item: any) => {
          const tyreSizeDetails = parseTireSizeArrow(item?.tyreSize);
          if (tyreSizeDetails && Object.keys(tyreSizeDetails).length === 0) return acc;
          acc[item?.tyreSize] = {
            variants: item?.variants,
            ...tyreSizeDetails,
          };
          return acc;
        },
        {}
      );
      if (converted && Object.keys(converted).length > 0) {
        setActiveTab(Object.keys(converted)[0]);
      }
      if (isMounted) setConvertedTyreSize(converted || {});
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [modelTyresize]);

  // Only render if convertedTyreSize is not empty
  if (Object.keys(convertedTyreSize).length === 0) {
    return null;
  }

  return (
    <div className={styles.rootContainer}>
      {convertedTyreSize && (
        <div className={styles.container}>
          <div className={styles.content}>
            <Text className={styles.title}>Tyre size options</Text>
            <div className={styles.imageContainerMobile}>
              <ImageResponsiveAssets
                filePath={`/images/dimensions`}
                name="tyre-size"
                alt="tire size image"
                width={448}
                height={336}
                loading="lazy"
                className={styles.imageDesktop}
              />
              <BootCapacityTextBox
                firstText={
                  <Text>
                    {`Available in ${numberToWords(
                      Object?.keys(convertedTyreSize)?.length
                    )} tyre sizes - `}
                    {Object?.keys(convertedTyreSize)?.map((size, idx, arr) => (
                      <span key={size}>
                        {size}
                        {idx < arr.length - 2
                          ? ", "
                          : idx === arr.length - 2
                          ? ", and "
                          : "."}
                      </span>
                    ))}
                  </Text>
                }
                className={styles.bootCapacityTextBox}
              />
            </div>
            <div className={styles.tireSizeButtonsContainer}>
              <div className={styles.buttons}>
                {Object?.keys(convertedTyreSize)?.map((tyreCode, idx) => (
                  <TireSizeButtons
                    key={tyreCode}
                    tyreCode={tyreCode}
                    active={activeTab === tyreCode}
                    onClick={() => handleClick(tyreCode)}
                  />
                ))}
              </div>
              {Object?.keys(convertedTyreSize)?.map((tyreSize, idx) => (
                tyreSize && <TireSizeTextBlock
                  key={convertedTyreSize[tyreSize].original}
                  className={`${styles.tireSizeTextBlockMobile} ${
                    activeTab === tyreSize
                      ? "active"
                      : styles.tireSizeTextBlockMobileHidden
                  }`}
                  tyreCode={convertedTyreSize[tyreSize].original}
                  points={convertedTyreSize[tyreSize].points}
                />
              ))}
            </div>
          </div>
          <div className={styles.imageContainerDesktop}>
            <ImageResponsiveAssets
              filePath={`/images/dimensions`}
              name="tyre-size"
              alt="tire size image"
              width={448}
              height={336}
              loading="lazy"
              className={styles.imageDesktop}
            />
            <BootCapacityTextBox
              firstText={
                <Text>
                  {`Available in ${numberToWords(
                    Object?.keys(convertedTyreSize)?.length
                  )} tyre sizes - `}
                  {Object.keys(convertedTyreSize)?.map((size, idx, arr) => (
                    <span key={size}>
                      {size}
                      {idx < arr.length - 2
                        ? ", "
                        : idx === arr.length - 2
                        ? ", and "
                        : "."}
                    </span>
                  ))}
                </Text>
              }
              className={styles.bootCapacityTextBox}
            />
          </div>
        </div>
      )}
      {Object?.keys(convertedTyreSize)?.map((tyreSize) => {
        return (
          <div
            key={tyreSize}
            className={`${styles.carousel} ${
              activeTab === tyreSize
                ? "active"
                : styles.tireSizeTextBlockMobileHidden
            }`}
          >
            <Carousel
              heading={`Variants with tire size ${tyreSize}?`}
              carouselWrapperClassName={styles.carouselWrapper}
              headingWrapperClassName={styles.headingWrapperCarousel}
              headingClassName={styles.headingCarousel}
              carouselItemClassName={styles.carouselItem}
            >
              {convertedTyreSize[tyreSize]?.variants?.map(
                (variant: any, idx: number) => {
                  return (
                    <TireSizeVariantCard
                      key={variant.id || idx}
                      expressDeliveryDate={formatDeliveryDate(
                        variant?.minDeliveryDays || 0
                      )}
                      standardDeliveryDate={formatDeliveryDate(
                        variant?.minStandardDeliveryDays || 0
                      )}
                      isLoggedin={isLoggedIn}
                      title={variant?.variant?.name}
                      fuelType={variant?.fuelType}
                      transmission={variant?.transmission}
                      price={`${formatPrice(variant?.minColorPrice)}`}
                      location={`On-road price in ${cityName}`}
                      variantSlug={variant?.slug}
                      isExpress={variant?.expressDeliveryFlag}
                      status={variant.status}
                      makeName={makeName}
                      modelName={modelName}
                      pageType={pageType}
                      cityName={cityName}
                    />
                  );
                }
              )}
            </Carousel>
          </div>
        );
      })}
    </div>
  );
}
